import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { completeSignIn, readCallbackParams } from '@/lib/console-auth.ts';
import type { ConsoleAuthConfig } from '@/lib/console-auth.ts';
import { credentialFor, useSession } from '@/session.tsx';

/**
 * Where Cognito sends the browser back.
 *
 * Two things happen and both matter. The code is exchanged for an ID token — which needs the PKCE
 * verifier this tab put in `sessionStorage` before it left, and is why that one slot exists — and
 * the URL is then **replaced**, not pushed, so the authorization code does not survive in history
 * for the back button to re-present.
 *
 * `ran` guards the exchange rather than an empty dependency array: an authorization code is
 * single-use, and StrictMode mounts every effect twice in development. Without it the second run
 * exchanges a spent code and the sign-in fails only when a developer is watching, which is the
 * worst possible time for it to look broken.
 */
export function Callback({ authConfig }: { authConfig: ConsoleAuthConfig | null }) {
  const { authenticate } = useSession();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    if (!authConfig) {
      setError('This console is not configured for account sign-in.');
      return;
    }

    const params = readCallbackParams(window.location.search);

    void completeSignIn(authConfig, params)
      .then(({ identity, returnTo }) => {
        authenticate(credentialFor(identity));
        window.history.replaceState(null, '', returnTo);
      })
      .catch((cause: unknown) => {
        // The code is spent either way, so it must not stay in the URL for a retry to reuse.
        window.history.replaceState(null, '', '/');
        setError(cause instanceof Error ? cause.message : String(cause));
      });
  }, [authConfig, authenticate]);

  if (error) {
    return (
      <div className="grid min-h-dvh place-items-center px-4 py-10">
        <Card className="w-full max-w-md">
          <CardHeader>
            <AlertTriangle className="text-destructive size-6" />
            <CardTitle>Sign-in did not complete</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => window.location.assign('/')}>Back to sign in</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="text-muted-foreground grid min-h-dvh place-items-center gap-2 px-4">
      <div className="flex items-center gap-2 text-sm">
        <Loader2 className="size-4 animate-spin" />
        Completing sign-in…
      </div>
    </div>
  );
}
